import { useCallback, useState } from 'react';

const isPlainPrimaryClick = (e) => !(e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0);

/**
 * Shows a clean href (e.g. without tracking params) by default, and switches to the full url
 * right before the browser reads href for a native action: middle click, context menu,
 * long press and keyboard focus.
 *
 * @param {Object} params
 * @param {string} params.cleanHref - href displayed on hover
 * @param {string} params.fullUrl - href used for any native browser action
 * @param {Function} [params.onMouseDown] - extra mouse down handler
 * @param {Function} [params.onClick] - called only for a plain primary click. Modified clicks
 *   (cmd/ctrl/shift/alt) and non primary buttons are left to the browser.
 * @returns {{ href: string, handlers: Object }}
 */
export const useCleanHref = ({ cleanHref, fullUrl, onMouseDown, onClick }) => {
    const [isFullUrlExposed, setIsFullUrlExposed] = useState(false);
    const exposeFullUrl = useCallback(() => setIsFullUrlExposed(true), []);

    const handleMouseDown = useCallback(
        (e) => {
            exposeFullUrl();
            onMouseDown?.(e);
        },
        [exposeFullUrl, onMouseDown]
    );

    const handleClick = useCallback(
        (e) => {
            if (isPlainPrimaryClick(e)) {
                onClick?.(e);
            }
        },
        [onClick]
    );

    return {
        href: isFullUrlExposed ? fullUrl : cleanHref,
        handlers: {
            onMouseDown: handleMouseDown,
            onContextMenu: exposeFullUrl,
            onTouchStart: exposeFullUrl,
            onFocus: exposeFullUrl,
            onClick: handleClick,
        },
    };
};
